import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { SpacerQaqcTitleValueDto } from '../../services/project-details.service';

interface SelectableTitle extends SpacerQaqcTitleValueDto {
  isSelected: boolean;
}

interface TitleGroup {
  groupId: number;
  groupName: string;
  isSelected: boolean;
  titles: SelectableTitle[];
}

export interface PickSpacerQaqcTitlesDialogData {
  predefinedTitles: SpacerQaqcTitleValueDto[];
  alreadyAddedTitles: SpacerQaqcTitleValueDto[];
}

@Component({
  selector: 'app-pick-spacer-qaqc-titles-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './pick-spacer-qaqc-titles-dialog.component.html',
  styleUrls: ['./pick-spacer-qaqc-titles-dialog.component.css'],
})
export class PickSpacerQaqcTitlesDialogComponent implements OnInit {
  addedTitles: SelectableTitle[] = [];
  groupedTitles: TitleGroup[] = [];
  otherTitles: SelectableTitle[] = [];

  constructor(
    public dialogRef: MatDialogRef<PickSpacerQaqcTitlesDialogComponent, SpacerQaqcTitleValueDto[] | null>,
    @Inject(MAT_DIALOG_DATA) public data: PickSpacerQaqcTitlesDialogData
  ) { }

  ngOnInit(): void {
    const predefined = this.data.predefinedTitles ?? [];
    const alreadyAdded = this.data.alreadyAddedTitles ?? [];

    // Show all already added titles including duplicates
    this.addedTitles = alreadyAdded.map((t) => ({ ...t, isSelected: true }));

    const groupedMap = new Map<number, TitleGroup>();
    const other: SelectableTitle[] = [];

    for (const title of predefined) {
      if (title.groupId == null) {
        other.push({
          ...title,
          isSelected: false,
        });
        continue;
      }

      // Items in groups are always unchecked initially, even if already added
      const selectable: SelectableTitle = {
        ...title,
        isSelected: false,
      };

      const existingGroup = groupedMap.get(title.groupId);
      if (existingGroup) {
        existingGroup.titles.push(selectable);
      } else {
        groupedMap.set(title.groupId, {
          groupId: title.groupId,
          groupName: title.groupName ?? `Group ${title.groupId}`,
          isSelected: false,
          titles: [selectable],
        });
      }
    }

    this.groupedTitles = Array.from(groupedMap.values())
      .sort((a, b) => a.groupId - b.groupId)
      .map((group) => {
        group.titles = group.titles.sort((a, b) => a.id - b.id);
        this.syncGroupSelection(group);
        return group;
      });

    this.otherTitles = other;
  }

  onGroupSelectionChanged(group: TitleGroup): void {
    for (const title of group.titles) {
      title.isSelected = group.isSelected;
    }
  }

  onGroupTitleSelectionChanged(group: TitleGroup): void {
    this.syncGroupSelection(group);
  }

  private syncGroupSelection(group: TitleGroup): void {
    group.isSelected = group.titles.length > 0 && group.titles.every((title) => title.isSelected);
  }

  onOk(): void {
    const selectedFromGroups = this.groupedTitles.flatMap((g) => g.titles.filter((t) => t.isSelected));
    const selectedFromOther = this.otherTitles.filter((t) => t.isSelected);
    const selected = [...selectedFromGroups, ...selectedFromOther].map((t) => ({
      id: t.id,
      title: t.title,
      groupId: t.groupId,
      groupName: t.groupName,
    }));

    this.dialogRef.close(selected);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
